import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { projectsAPI } from '../../services/api';

const CreateProject = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  
  // API calls are now handled through the centralized API service

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Project name is required')
      .max(100, 'Project name cannot exceed 100 characters'),
    description: Yup.string()
      .max(500, 'Description cannot exceed 500 characters')
  });

  // Initial form values
  const initialValues = {
    name: '',
    description: ''
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setError('');
    
    try {
      const response = await projectsAPI.createProject(values);
      
      if (response.data.status === 201) {
        // Project created successfully, redirect to project page
        navigate(`/projects/${response.data.data.project_id}`);
      } else {
        setError(response.data.message || 'Failed to create project');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      
      if (err.response) {
        setError(err.response.data.message || 'Failed to create project');
      } else {
        setError('Network error. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Create New Project</h1>
          
          {error && <Alert variant="danger">{error}</Alert>}
        </Col>
      </Row>
      
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ 
                  handleSubmit, 
                  handleChange, 
                  handleBlur, 
                  values, 
                  touched, 
                  errors, 
                  isSubmitting 
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.name && !!errors.name}
                        placeholder="Enter project name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.description && !!errors.description}
                        placeholder="Enter project description"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="secondary" 
                        onClick={() => navigate('/projects')}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to Projects
                      </Button>
                      
                      <Button 
                        type="submit" 
                        variant="primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            Create Project
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Project Guidelines</h5>
            </Card.Header>
            <Card.Body>
              <p>A project helps you organize related documents for review and analysis.</p>
              <ul>
                <li>Give your project a clear, descriptive name</li>
                <li>Add details in the description to help identify the project's purpose</li>
                <li>After creating a project, you can upload documents for review</li>
                <li>You can edit project details later if needed</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateProject;
